import { MessageCircle, Users, Send, Reply, AlertCircle } from 'lucide-react'

import PageContainer from '@/common/components/PageContainer'
import Heading from '@/common/shared/heading'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Skeleton } from '@/common/ui/skeleton'
import { Progress } from '@/common/ui/progress'
import { Badge } from '@/common/ui/badge'

import { useQueryChatQuota, useQueryChatAnalytics } from '../../api/hooks'

export function ChatAnalyticsPage() {
	const { data: quota, isLoading: quotaLoading } = useQueryChatQuota()
	const { data: analytics, isLoading: analyticsLoading } =
		useQueryChatAnalytics()

	// Calculate quota percentage from the nested quota object
	const quotaPercentage = quota?.quota
		? Math.round((quota.quota.totalUsage / quota.quota.quotaValue) * 100)
		: 0

	return (
		<PageContainer>
			<div className="mb-6">
				<Heading
					title="สถิติแชท"
					description="ดูสถิติการใช้งานแชท LINE และโควต้าข้อความ"
				/>
			</div>

			{/* Quota Card */}
			<Card className="mb-6">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Send className="h-5 w-5" />
						โควต้าข้อความ LINE
					</CardTitle>
					<CardDescription>การใช้งานข้อความ Push ในเดือนนี้</CardDescription>
				</CardHeader>
				<CardContent>
					{quotaLoading ? (
						<div className="space-y-4">
							<Skeleton className="h-4 w-full" />
							<Skeleton className="h-8 w-1/2" />
						</div>
					) : quota?.quota ? (
						<div className="space-y-4">
							<div className="flex items-center justify-between">
								<span className="text-2xl font-bold">
									{quota.quota.totalUsage.toLocaleString()}
								</span>
								<span className="text-muted-foreground">
									/ {quota.quota.quotaValue.toLocaleString()} ข้อความ
								</span>
							</div>
							<Progress value={quotaPercentage} className="h-3" />
							<div className="flex items-center justify-between text-sm">
								<span className="text-muted-foreground">
									ใช้ไปแล้ว {quotaPercentage}%
								</span>
								<span className="text-muted-foreground">
									คงเหลือ {quota.quota.remaining.toLocaleString()} ข้อความ
								</span>
							</div>
							{quotaPercentage >= 100 ? (
								<div className="flex items-center gap-2 rounded-lg bg-destructive/20 p-3 text-sm text-destructive">
									<AlertCircle className="h-4 w-4" />
									<span>
										โควต้าหมดแล้ว ไม่สามารถส่ง Push Message ได้
										กรุณาติดต่อผู้ดูแลระบบเพื่ออัพเกรดแพ็คเกจ
									</span>
								</div>
							) : quotaPercentage >= 80 ? (
								<div className="flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
									<AlertCircle className="h-4 w-4" />
									<span>
										โควต้าใกล้หมด กรุณาติดต่อผู้ดูแลระบบเพื่ออัพเกรดแพ็คเกจ
									</span>
								</div>
							) : null}
						</div>
					) : (
						<p className="text-muted-foreground">
							ไม่สามารถโหลดข้อมูลโควต้าได้
						</p>
					)}
				</CardContent>
			</Card>

			{/* Stats Grid */}
			<div className="mb-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
				<StatCard
					title="ห้องแชททั้งหมด"
					value={analytics?.totalRooms}
					icon={Users}
					isLoading={analyticsLoading}
				/>
				<StatCard
					title="ห้องที่ยังไม่อ่าน"
					value={analytics?.unreadRooms}
					icon={MessageCircle}
					isLoading={analyticsLoading}
				/>
				<StatCard
					title="ใช้ Reply Token"
					value={analytics?.replyTokenUsage}
					description="ข้อความฟรี"
					icon={Reply}
					isLoading={analyticsLoading}
					badge="ฟรี"
					badgeVariant="default"
				/>
				<StatCard
					title="ใช้ Push Message"
					value={analytics?.pushMessageUsage}
					description="ข้อความเสียเงิน"
					icon={Send}
					isLoading={analyticsLoading}
					badge="เสียเงิน"
					badgeVariant="destructive"
				/>
			</div>

			{/* Additional Stats */}
			<div className="grid gap-4 md:grid-cols-2">
				{/* Room Status */}
				<Card>
					<CardHeader>
						<CardTitle>สถานะห้องแชท</CardTitle>
						<CardDescription>ภาพรวมห้องแชททั้งหมด</CardDescription>
					</CardHeader>
					<CardContent>
						{analyticsLoading ? (
							<div className="space-y-4">
								<Skeleton className="h-16 w-full" />
								<Skeleton className="h-16 w-full" />
							</div>
						) : analytics ? (
							<div className="space-y-4">
								<div className="flex items-center justify-between rounded-lg border p-4">
									<div className="flex items-center gap-3">
										<div className="rounded-full bg-green-100 p-2 dark:bg-green-900">
											<Users className="h-5 w-5 text-green-600 dark:text-green-400" />
										</div>
										<div>
											<p className="font-medium">ห้องที่ Active</p>
											<p className="text-sm text-muted-foreground">
												มีการสนทนาล่าสุด
											</p>
										</div>
									</div>
									<span className="text-2xl font-bold">
										{analytics.activeRooms?.toLocaleString() || 0}
									</span>
								</div>
								<div className="flex items-center justify-between rounded-lg border p-4">
									<div className="flex items-center gap-3">
										<div className="rounded-full bg-orange-100 p-2 dark:bg-orange-900">
											<MessageCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
										</div>
										<div>
											<p className="font-medium">รอการตอบกลับ</p>
											<p className="text-sm text-muted-foreground">
												ยังไม่ได้อ่าน
											</p>
										</div>
									</div>
									<span className="text-2xl font-bold">
										{analytics.unreadRooms?.toLocaleString() || 0}
									</span>
								</div>
							</div>
						) : (
							<p className="text-muted-foreground">ไม่มีข้อมูล</p>
						)}
					</CardContent>
				</Card>

				{/* Message Cost */}
				<Card>
					<CardHeader>
						<CardTitle>ต้นทุนข้อความ</CardTitle>
						<CardDescription>
							สัดส่วนการใช้ Reply Token vs Push Message
						</CardDescription>
					</CardHeader>
					<CardContent>
						{analyticsLoading ? (
							<div className="space-y-4">
								<Skeleton className="h-16 w-full" />
								<Skeleton className="h-16 w-full" />
							</div>
						) : analytics ? (
							<div className="space-y-4">
								<div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
									<div className="flex items-center gap-3">
										<Reply className="h-5 w-5 text-green-600" />
										<div>
											<p className="font-medium text-green-800 dark:text-green-200">
												Reply Token
											</p>
											<p className="text-sm text-green-600 dark:text-green-400">
												ไม่เสียค่าใช้จ่าย
											</p>
										</div>
									</div>
									<span className="text-2xl font-bold text-green-700 dark:text-green-300">
										{analytics.replyTokenUsage?.toLocaleString() || 0}
									</span>
								</div>
								<div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950">
									<div className="flex items-center gap-3">
										<Send className="h-5 w-5 text-red-600" />
										<div>
											<p className="font-medium text-red-800 dark:text-red-200">
												Push Message
											</p>
											<p className="text-sm text-red-600 dark:text-red-400">
												หักจากโควต้า
											</p>
										</div>
									</div>
									<span className="text-2xl font-bold text-red-700 dark:text-red-300">
										{analytics.pushMessageUsage?.toLocaleString() || 0}
									</span>
								</div>
							</div>
						) : (
							<p className="text-muted-foreground">ไม่มีข้อมูล</p>
						)}
					</CardContent>
				</Card>
			</div>
		</PageContainer>
	)
}

interface StatCardProps {
	title: string
	value?: number
	description?: string
	icon: React.ElementType
	isLoading?: boolean
	badge?: string
	badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline'
}

function StatCard({
	title,
	value,
	description,
	icon: Icon,
	isLoading,
	badge,
	badgeVariant = 'default',
}: StatCardProps) {
	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
				<CardTitle className="text-sm font-medium">{title}</CardTitle>
				<Icon className="h-4 w-4 text-muted-foreground" />
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<Skeleton className="h-8 w-24" />
				) : (
					<div className="flex items-center gap-2">
						<span className="text-2xl font-bold">
							{value?.toLocaleString() ?? '-'}
						</span>
						{badge && (
							<Badge variant={badgeVariant} className="text-xs">
								{badge}
							</Badge>
						)}
					</div>
				)}
				{description && (
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				)}
			</CardContent>
		</Card>
	)
}
